import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getMessages, sendMessage, markMessageRead } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, Send, Loader2, User, Clock } from 'lucide-react';
import { toast } from 'sonner';

const MessagesPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const data = await getMessages();
      setMessages(data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedMessages = messages.reduce((acc, msg) => {
    const partnerId = msg.sender_id === user?.id ? msg.recipient_id : msg.sender_id;
    const partnerName = msg.sender_id === user?.id ? 'Destinataire' : msg.sender_name;
    
    if (!acc[partnerId]) {
      acc[partnerId] = {
        partnerId,
        partnerName,
        messages: []
      };
    }
    acc[partnerId].messages.push(msg);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="space-y-6" data-testid="messages-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
            Messages
          </h1>
          <p className="text-slate-500 mt-1">
            Communiquez avec vos partenaires
          </p>
        </div>

        {/* Messages */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : messages.length > 0 ? (
          <div className="space-y-4">
            {Object.values(groupedMessages).map((group) => (
              <Card key={group.partnerId}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{group.partnerName}</CardTitle>
                      <p className="text-xs text-slate-500">
                        {group.messages.length} message(s)
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {group.messages.slice(0, 3).map((msg) => (
                      <div 
                        key={msg.id}
                        className={`p-3 rounded-lg ${
                          msg.sender_id === user?.id 
                            ? 'bg-orange-50 ml-8' 
                            : 'bg-slate-50 mr-8'
                        }`}
                      >
                        <p className="text-sm text-slate-700">{msg.content}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                          <Clock className="w-3 h-3" />
                          {new Date(msg.created_at).toLocaleString('fr-FR')}
                          {!msg.read && msg.recipient_id === user?.id && (
                            <Badge className="bg-blue-100 text-blue-600 text-xs">Nouveau</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun message</h3>
                <p className="text-slate-500">
                  Les conversations avec vos partenaires apparaîtront ici
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default MessagesPage;
